<template>
  <div>
    <div class="container">
      <div class="columns">
        <div class="column is-three-fifths is-offset-one-fifth">
          <h1 class="title is-2 fancy is-spaced">Settings</h1>
          <h2 class="subtitle is-5">Account</h2>
          <div class="account-option">
            <h3 class="title is-6">Email</h3>
            <div class="subtitle is-6">{{ userEmail }}</div>
          </div>
          <div class="account-option">
            <subscription-settings
              :hasSubscription="hasSubscription"
              :loading="subscriptionIsLoading"
              :resubscribe="resubscribeUser"
              :subscription="userSubscription"
              :unsubscribe="unsubscribeUser" />
          </div>
          <div
            class="account-option"
            v-if="hasSubscription && !userSubscription.cancelAtPeriodEnd"
          >
            <stored-card :subscription="userSubscription"></stored-card>
          </div>
          <hr />
          <notification-settings />
          <hr />
          <user-setting title="Prompts" :loading="userIsLoading || subscriptionIsLoading">
            <prompt-settings
              :has-subscription="hasSubscription"
              :show-premium-modal="showPremiumModal"
            />
          </user-setting>
          <hr />
          <div v-if="lines.length">
            <h2 class="subtitle is-5">Backup</h2>
            <button class="button is-primary is-rounded" @click="generateCsv">Download CSV</button>
            <a class="download-link" ref="downloadLink" :download="filename" :href="csvUrl"></a>
            <hr />
          </div>
          <div class="columns is-centered is-spaced">
            <button class="button has-text-danger is-text is-small" @click="confirmDelete">
              Delete my account
            </button>
          </div>
        </div>
      </div>
    </div>
    <premium-modal
      v-if="premiumModalTitle"
      :feature="premiumModalTitle"
      :on-close="handlePremiumModalClose"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import Papa from 'papaparse'
import NotificationSettings from '@/components/Settings/NotificationSettings.vue'
import PromptSettings from '@/components/Settings/PromptSettings.vue'
import SubscriptionSettings from '@/components/Settings/SubscriptionSettings.vue'
import UserSetting from '@/components/Settings/UserSetting.vue'
import StoredCard from '@/components/Subscription/StoredCard.vue'
import PremiumModal from '@/components/Premium/PremiumModal.vue'
import { displayError, trackClick } from '@/util'

export default {
  name: 'Settings',
  components: {
    NotificationSettings,
    PremiumModal,
    PromptSettings,
    StoredCard,
    SubscriptionSettings,
    UserSetting,
  },
  data() {
    return {
      csvUrl: '',
      premiumModalTitle: null,
    }
  },
  mounted() {
    if (!this.lines.length) {
      this.getLines()
    }
  },
  computed: {
    ...mapGetters([
      'hasSubscription',
      'lines',
      'subscriptionIsLoading',
      'userEmail',
      'userLoginMethod',
      'userSettings',
      'userSubscription',
      'userIsLoading',
    ]),
    filename() {
      return `tinythoughts_backup_${moment().format()}`
    },
  },
  methods: {
    ...mapActions([
      'deleteUser',
      'reauthenticateAndDeleteUser',
      'resubscribeUser',
      'unsubscribeUser',
      'getLines'
    ]),
    generateCsv() {
      const lines = this.lines.reduce((acc, [date, entries]) => {
        const newLines = entries.map(line => ({ date: `${date}, ${line.years}`, text: line.text }))
        return [...acc, ...newLines]
      }, [])
      const csvContent = Papa.unparse(lines)
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      this.csvUrl = URL.createObjectURL(blob)
      if (navigator.msSaveBlob) { // 💩
        navigator.msSaveBlob(blob, this.filename)
      } else {
        this.$nextTick(() => {
          this.$refs.downloadLink.click()
        })
      }
    },
    handlePremiumModalClose() {
      this.premiumModalTitle = null
    },
    confirmDelete() {
      trackClick({ label: 'deleteAccount' })
      this.$buefy.dialog.confirm({
        title: 'Delete account',
        message: 'Are you sure you want to <b>delete</b> your account? All posts and photos will be permanently deleted. This action cannot be undone.',
        confirmText: 'Delete Account',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: this.delete
      })
    },
    showConfirmPassword() {
      this.$buefy.dialog.prompt({
        confirmText: 'Delete Account',
        message: 'This operation is sensitive and requires recent authentication. Your password is required to continue.',
        inputAttrs: {
          placeholder: 'Password',
          type: 'password'
        },
        trapFocus: true,
        type: 'is-danger',
        onConfirm: this.reauthenticateAndDelete
      })
    },
    async delete() {
      console.log('the login method for this user is', this.userLoginMethod)
      trackClick({ label: 'deleteAccountConfirmed' })
      try {
        await this.deleteUser()
      } catch (err) {
        console.log(err)
        if (err.code === 'auth/requires-recent-login') {
          if (this.userLoginMethod === 'password') {
            // show dialog to get password
            this.showConfirmPassword()
          } else {
            this.reauthenticateAndDelete()
          }
        }
      }
    },
    async reauthenticateAndDelete(password) {
      try {
        await this.reauthenticateAndDeleteUser(password)
      } catch (err) {
        displayError(err)
        if (password) {
          this.showConfirmPassword()
        }
      }
    },
    showPremiumModal(title) {
      this.premiumModalTitle = title
    }
  }
};
</script>

<style scoped lang="scss">
.container {
  padding: 0 20px;
}

.download-link {
  visibility: hidden;
}

.account-option {
  margin: 20px 0;
}
</style>
